import { type Metric, type MetricFragment } from "../metrics";
import { isLeafNode } from "../utils/tree";

import { type Breakdown } from "./breakdown";

export interface All {
  type: "all";
}

export interface Individuals {
  type: "individuals";
  individuals: Record<string, string>[];
}

export interface IndividualsMatching {
  type: "individualsMatching";
  param: Breakdown[];
}

export interface GroupedBy {
  type: "groupedBy";
  param: Breakdown[];
}

export type Subject = All | Individuals | IndividualsMatching | GroupedBy;

export interface ChangeThreshold {
  type: "relative" | "absoluteOver" | "absoluteUnder";
  value: number;
}

export interface TriggerConditionsV1 {
  version: undefined;
  metric: Metric;
  alarmConditions: AlarmConditions;
  subject: Subject;
}

export interface TriggerConditionsV2 {
  version: "v2";
  subject: Subject;
  alarmCondition: AlarmConditionV2;
}

export type TriggerConditions = TriggerConditionsV1 | TriggerConditionsV2;

export interface CompareTo {
  type: "windowEndingDaysAgo" | "averageOfPreviousWindowOfLengthDays";
  value: number;
}

export interface AlarmConditions {
  windowSizeDays: number;
  changeThreshold: ChangeThreshold;
  compareTo: CompareTo | null;
}

export type AlarmConditionV2 =
  | CompositeAlarmConditionV2
  | SimpleAlarmConditionV2;

export interface CompositeAlarmConditionV2 {
  version: "v2";
  isLeaf: false;
  combinator: "and" | "or";
  children: AlarmConditionV2[];
}

export interface SimpleAlarmConditionV2 {
  version: "v2";
  isLeaf: true;
  metric: Pick<Metric, "id" | "name" | "format">;
  currentValue: AveragedValueConfig;
  referenceValue: MetricValueConfig;
  anomalyCondition: AnomalyCondition;
}

export function isSimpleAlarmCondition(
  obj: AlarmConditionV2,
): obj is SimpleAlarmConditionV2 {
  return isLeafNode(obj);
}

export interface FixedValueConfig {
  type: "FixedValueConfig";
  value: number;
}

export interface AveragedValueConfig {
  type: "AveragedValueConfig";
  windowStartOffsetDays: number;
  windowEndOffsetDays: number;
  averagingMode: AveragingMode;
}

export type SkipReason =
  | "EXCLUDED_FROM_ALERTING"
  | "ALL_AD_OBJECTS_NOT_ACTIVE"
  | "METRIC_VALUE_MISSING"
  | "NO_COMPARISON_VALUE"
  | "NO_PRESENT_VALUE";

export interface AlertPreviewRowV2 {
  version: "v2";
  runAt: string;
  subjectItem: any;
  subjectItemName: string;
  conditionTriggered: boolean;
  skipReason: SkipReason | null;
  resultRow: AlarmConditionResultRow;
  averageDailySpend: number;
  numAdObjectsFound: number;
  numAdObjectsSkipped: number;
}

export type MetricValueConfig = FixedValueConfig | AveragedValueConfig;

export interface UniformAveragingMode {
  type: "UniformAveragingMode";
}

/* export interface ExponentialAveragingMode {
     type: "ExponentialAveragingMode";
     decay: number;
   }

   export interface DoubleExponentialAveragingMode {
     type: "DoubleExponentialAveragingMode";
     decay: number;
     slopeDecay: number;
   } */

export type AveragingMode = UniformAveragingMode;
/* | ExponentialAveragingMode
   | DoubleExponentialAveragingMode; */

export interface AboveReferenceValue {
  type: "AboveReferenceValue";
}

export interface BelowReferenceValue {
  type: "BelowReferenceValue";
}

export interface OutsidePercentageOfReferenceValue {
  type: "OutsidePercentageOfReferenceValue";
  percentage: number;
}

export type AnomalyCondition =
  | AboveReferenceValue
  | BelowReferenceValue
  | OutsidePercentageOfReferenceValue;

export interface AllSubjects {
  type: "all";
}

export interface Grouped {
  type: "grouped";
  groups: { key: string; value: string }[];
}

export interface Individual {
  type: "individual";
  ad_key: any;
}

export type SubjectItem = AllSubjects | Grouped | Individual;

export type SimpleAlarmConditionResultRow = {
  isLeaf: true;
  conditionTriggered: boolean;
  runAt: string;
  subjectItem: any;
  subjectItemName: string;

  metric: MetricFragment;

  /* used to indicate there is missing data (we did not compute the metric value
     for a particular subjectItem).
     If this flag is true, the values set below are not meaningful. */
  isMissing: boolean;

  // These are stringified Numbers. This allows us to encode ±Infinity and NaN
  currentValueComputed: string;
  referenceValueComputed: string;
  normalityBounds: [string, string];
};

export type CompositeAlarmConditionResultRow = {
  isLeaf: false;
  conditionTriggered: boolean;
  runAt: string;
  subjectItem: any;
  subjectItemName: string;
  isMissing: boolean;
  combinator: "and" | "or";
  children: AlarmConditionResultRow[];
};

export type AlarmConditionResultRow =
  | SimpleAlarmConditionResultRow
  | CompositeAlarmConditionResultRow;

export interface IncidentTemplates {
  description?: string;
  clickToExpandSection?: string;
}

export interface ActionItemsConfig {
  clickToExpandSection?: string;
}
